/**
 * Candle volume-spike markers:
 *   🐋  Vest NQ-PERP volume spike (utils/vestSpikes.ts)
 *   🦈  CME volume spike (utils/cmeVolumeSpikes.ts, same rule as the
 *       volume-pane "2.4x")
 *   🐋🦈 both spiked on the same closed bar AND their taker deltas agree:
 *       above the candle when both net bought, below when both net sold.
 * Spikes whose deltas disagree (or one delta unknown) stay separate, each on
 * its own delta's side. The combined marker has its own toggle and shows
 * even with the single toggles off; with it off, agreeing spikes are drawn
 * separately (if their toggles are on).
 *
 *   🐱  the Vest bar sticks out above the CME bar in the volume pane
 *       (utils/vestCmeCat.ts). Its
 *       own marker, independent of the spike toggles, on the Vest delta's
 *       side; stacks next to a 🐋/🦈/🐋🦈 on the same candle side.
 *
 *   🪝  Wick rejection, possible reversal (utils/wickRejection.ts). At the
 *       wick tip (lower wick under the low, upper wick over the high), its own
 *       toggle; the glyph is the user's pick, half opacity until confirmed,
 *       larger with an amber halo when strong; broken (tip traded before a
 *       confirming close) = half size, quarter opacity, no halo.
 */
import type { RelVol } from './cmeVolumeSpikes';
import { basisDetail } from './relVolBaseline';
import type { VestSpike } from './vestSpikes';
import { vestCatTooltip, type VestCat } from './vestCmeCat';
import { DEFAULT_WICK_SETTINGS, wickTooltip, type GradedWick } from './wickRejection';

export type Side = 'above' | 'below';

export interface CandleSpikeToggles {
  vest: boolean;
  cme: boolean;
  both: boolean;
}

export interface CandleSpikeMarker {
  id: string;
  /** Chart bar open, seconds. */
  time: number;
  kind: 'vest' | 'cme' | 'both' | 'cat' | 'wick';
  side: Side;
  vest?: VestSpike;
  cme?: RelVol & { delta: number | null };
  cat?: VestCat;
  wick?: GradedWick;
  /** Glyph override (the 🪝 is user-selectable). */
  glyph?: string;
}

const sideOf = (delta: number | null | undefined): Side => (delta != null && delta < 0 ? 'below' : 'above');
const sign = (d: number | null | undefined): number => (d == null || d === 0 ? 0 : Math.sign(d));

export function candleSpikeMarkers(
  vestSpikes: readonly VestSpike[],
  cmeSpikes: readonly RelVol[],
  cmeDelta: ReadonlyMap<number, number>,
  toggles: CandleSpikeToggles,
  cats: readonly VestCat[] = [],
  wicks: readonly GradedWick[] = [],
  wickEmoji: string = DEFAULT_WICK_SETTINGS.emoji,
): CandleSpikeMarker[] {
  const vestAt = new Map(vestSpikes.map((s) => [s.time, s]));
  const cmeAt = new Map(cmeSpikes.map((r) => [r.time, { ...r, delta: cmeDelta.has(r.time) ? (cmeDelta.get(r.time) as number) : null }]));
  const times = [...new Set([...vestAt.keys(), ...cmeAt.keys()])].sort((a, b) => a - b);
  const out: CandleSpikeMarker[] = [];
  for (const t of times) {
    const v = vestAt.get(t);
    const c = cmeAt.get(t);
    const agree = !!v && !!c && sign(v.delta) !== 0 && sign(v.delta) === sign(c.delta);
    if (agree && toggles.both) {
      out.push({ id: `both:${t}`, time: t, kind: 'both', side: sideOf(v!.delta), vest: v, cme: c });
      continue;
    }
    if (v && toggles.vest) out.push({ id: `vest:${t}`, time: t, kind: 'vest', side: sideOf(v.delta), vest: v });
    if (c && toggles.cme) out.push({ id: `cme:${t}`, time: t, kind: 'cme', side: sideOf(c.delta), cme: c });
  }
  for (const cat of cats) out.push({ id: `cat:${cat.time}`, time: cat.time, kind: 'cat', side: cat.side, cat });
  for (const w of wicks) out.push({ id: `wick:${w.time}`, time: w.time, kind: 'wick', side: w.side, wick: w, glyph: wickEmoji });
  return out.sort((a, b) => a.time - b.time);
}

export const GLYPH: Record<CandleSpikeMarker['kind'], string> = { vest: '🐋', cme: '🦈', both: '🐋🦈', cat: '🐱', wick: DEFAULT_WICK_SETTINGS.emoji };

export const glyphOf = (m: Pick<CandleSpikeMarker, 'kind' | 'glyph'>): string => m.glyph ?? GLYPH[m.kind];

/** Opacity of a broken (invalidated) wick mark: well under the 0.5 of pending / expired. */
export const BROKEN_WICK_ALPHA = 0.25;
/** Size of a broken wick mark relative to the base pill (never larger, never haloed). */
export const BROKEN_WICK_SCALE = 0.5;

const isBrokenWick = (m: Pick<CandleSpikeMarker, 'kind' | 'wick'>): boolean => m.kind === 'wick' && m.wick?.state === 'invalidated';

/** Draw opacity: a wick mark is half strength until a later close confirms it, a quarter once broken. */
export const markerAlpha = (m: Pick<CandleSpikeMarker, 'kind' | 'wick'>): number =>
  isBrokenWick(m) ? BROKEN_WICK_ALPHA : m.kind === 'wick' && m.wick?.state !== 'confirmed' ? 0.5 : 1;

/** Larger, haloed pill: 🐋🦈, and a strong wick mark that is not broken. */
export const isEmphasised = (m: Pick<CandleSpikeMarker, 'kind' | 'wick'>): boolean =>
  m.kind === 'both' || (m.kind === 'wick' && !!m.wick?.strong && !isBrokenWick(m));

/** Pill size multiplier on the base font: broken wick marks are drawn small. */
export const markerScale = (m: Pick<CandleSpikeMarker, 'kind' | 'wick'>): number => (isBrokenWick(m) ? BROKEN_WICK_SCALE : 1);

const fmt = (v: number): string => (Math.abs(v) >= 100 ? Math.round(v).toLocaleString('en-US') : v.toFixed(2));
const signed = (v: number): string => `${v >= 0 ? '+' : ''}${fmt(v)}`;

export function candleSpikeTooltip(m: CandleSpikeMarker, contract: string): { title: string; lines: string[] } {
  if (m.kind === 'cat' && m.cat) return vestCatTooltip(m.cat, contract);
  if (m.kind === 'wick' && m.wick) return wickTooltip(m.wick, glyphOf(m));
  const lines: string[] = [];
  if (m.cme) {
    lines.push(`CME vol ${fmt(m.cme.v)} ${contract} = ${m.cme.relVol.toFixed(1)}x avg ${fmt(m.cme.avg)} (${basisDetail(m.cme)})`);
    lines.push(m.cme.delta == null ? 'CME delta: n/a' : `CME delta ${signed(m.cme.delta)} (ask - bid prints)`);
  }
  if (m.vest) {
    lines.push(`Vest NQ-PERP vol ${fmt(m.vest.v)} = ${m.vest.relVol.toFixed(1)}x avg ${fmt(m.vest.avg)} (${basisDetail(m.vest)})`);
    lines.push(m.vest.delta == null ? 'Vest delta: n/a (kline-derived)' : `Vest delta ${signed(m.vest.delta)} (taker buy - sell)`);
  }
  if (m.kind === 'both') lines.push(`Both venues spiked with ${m.side === 'above' ? 'net buying' : 'net selling'}. Closed bar; descriptive, not a signal.`);
  else lines.push('Closed bar only. Descriptive, not a signal.');
  const title =
    m.kind === 'both'
      ? `🐋🦈 Both agree: CME ${m.cme!.relVol.toFixed(1)}x, Vest ${m.vest!.relVol.toFixed(1)}x`
      : m.kind === 'vest'
        ? `🐋 Vest vol ${m.vest!.relVol.toFixed(1)}x`
        : `🦈 CME vol ${m.cme!.relVol.toFixed(1)}x`;
  return { title, lines };
}
